/**
 * The column folded to its glyphs, 64px wide.
 *
 * Measured off Salesforge: the same muted ground as the open column, items
 * centred, each one a 40px square with 8px corners holding a 20px glyph.
 * Resting glyphs are the subtle ink, hover is the stronger grey, and the screen
 * you are on is the selected grey with the glyph a step darker. Every item has
 * a tooltip with its name, and its key when it has one, because on the rail
 * the glyph is all there is.
 *
 * It draws from the same lists as the open column, handed in by `Sidebar`
 * rather than imported, so there is one list and no import cycle between the
 * two files.
 *
 * A COUNT BECOMES A DOT. There is no room for two digits beside a centred
 * glyph, and a number squeezed in at 8px is a smudge that reads as a rendering
 * fault. The dot says the same thing the badge says, which is that this row
 * wants you, and the exact figure is in the tooltip and one click away.
 */
import type { ComponentType, ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '../lib/cn'
import { Tooltip } from './Tooltip'
import { navBadge, rowEnd, type NavBadge, type NavCounts, type RenderedGroup } from './sidebar-model'
import type { IconProps } from './icons'
import type { NavItem } from './Sidebar'

export const RAIL_ITEM = 'relative grid h-10 w-10 place-items-center rounded-lg transition'
export const RAIL_IDLE = 'text-ink-subtle hover:bg-surface-hover-strong hover:text-ink-secondary'
export const RAIL_ACTIVE = 'bg-surface-selected text-ink-secondary'
const RAIL_GLYPH = 'h-[1.25rem] w-[1.25rem]'

/** What the bubble beside a rail item says: its name, then its key if it has one. */
function RailTip({ label, caps, badge }: { label: string; caps?: ReactNode; badge?: NavBadge | null }) {
  return (
    <span className="flex items-center gap-2">
      <span>{badge ? `${label}, ${badge.title}` : label}</span>
      {caps}
    </span>
  )
}

function CountDot({ badge }: { badge: NavBadge | null }) {
  if (badge === null) return null
  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full',
        badge.tone === 'accent' ? 'bg-accent' : 'bg-ink-subtle',
      )}
    />
  )
}

/**
 * A rail item that goes somewhere.
 *
 * `active` overrides the router's own idea of "here" for the one row that needs
 * it, Settings, which must stay dark on a settings screen that has a row of its
 * own. Everything else leaves it out and takes the NavLink's answer.
 */
export function RailLink({
  to,
  label,
  icon: Glyph,
  end,
  active,
  badge = null,
  caps,
  onNavigate,
}: {
  to: string
  label: string
  icon: ComponentType<IconProps>
  end?: boolean
  active?: boolean
  badge?: NavBadge | null
  caps?: ReactNode
  onNavigate?: () => void
}) {
  return (
    <Tooltip content={<RailTip label={label} caps={caps} badge={badge} />}>
      <NavLink
        to={to}
        end={end}
        onClick={onNavigate}
        aria-label={badge ? `${label}, ${badge.title}` : label}
        aria-current={active === undefined ? undefined : active ? 'page' : false}
        className={({ isActive }) => cn(RAIL_ITEM, (active ?? isActive) ? RAIL_ACTIVE : RAIL_IDLE)}
      >
        <Glyph className={RAIL_GLYPH} />
        <CountDot badge={badge} />
      </NavLink>
    </Tooltip>
  )
}

/** A rail item that does something here rather than going anywhere. */
export function RailButton({
  label,
  icon: Glyph,
  onClick,
  disabled,
  caps,
}: {
  label: string
  icon: ComponentType<IconProps>
  onClick: () => void
  disabled?: boolean
  caps?: ReactNode
}) {
  return (
    <Tooltip content={<RailTip label={label} caps={caps} />}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        className={cn(RAIL_ITEM, RAIL_IDLE, 'disabled:opacity-60')}
      >
        <Glyph className={RAIL_GLYPH} />
      </button>
    </Tooltip>
  )
}

/**
 * The nav glyphs, group by group, and the utility glyphs after them.
 *
 * The group names stay in the markup as screen reader text, because they still
 * name each list, and a hairline between groups does on screen what the words
 * do in the open column.
 *
 * THE LIST CAN BE HANDED OVER IN TWO RUNS, with one glyph between them. The
 * open column reads Threads, Discovery and Outreach, then the Prospecting rows,
 * then Database & Infrastructure, and a rail in a different order from the column it folds
 * would move things under somebody's hand. `between` is a node rather than a
 * row of the list because it stands for seven rows at once (a `RailLink` the
 * sidebar lights on any of them), which a group's one-row-per-item shape does
 * not carry. A caller that passes neither draws exactly what this drew before.
 *
 * `betweenNext` is a second module's glyph, Operator's since 2026-10-30, drawn
 * after `between` under a hairline of its own, so the rail reads Prospecting
 * then Operator as the open column does.
 */
export function CollapsedRail({
  groups,
  between,
  betweenNext,
  groupsAfter = [],
  counts,
  onNavigate,
  children,
}: {
  groups: readonly RenderedGroup<NavItem>[]
  /** One glyph drawn in the list after `groups`, under a hairline of its own. */
  between?: ReactNode
  /** A second glyph drawn after `between`, under a hairline of its own. */
  betweenNext?: ReactNode
  /** The groups drawn after `between`. Each still carries its own hairline. */
  groupsAfter?: readonly RenderedGroup<NavItem>[]
  counts: NavCounts
  onNavigate?: () => void
  /** The utility band, drawn under a rule of its own. */
  children?: ReactNode
}) {
  // Read once for every row: `rowEnd` lets a list page keep Company lists lit.
  const { pathname } = useLocation()
  // One function for both runs, so a group reads the same wherever it falls.
  const drawGroup = (group: RenderedGroup<NavItem>) => (
    <div
      key={group.heading}
      className={cn('flex w-full flex-col items-center gap-1', group.dividerAbove && 'mt-2 border-t border-line pt-2')}
    >
      <p className="sr-only">{group.heading}</p>
      <ul className="flex flex-col items-center gap-1">
        {group.items.map((item) => (
          <li key={item.to}>
            <RailLink
              to={item.to}
              end={rowEnd(item, pathname)}
              label={item.label}
              icon={item.icon}
              badge={navBadge(item.badge, counts)}
              onNavigate={onNavigate}
            />
          </li>
        ))}
      </ul>
    </div>
  )

  return (
    <>
      <nav aria-label="Main" className="flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto py-2">
        {groups.map(drawGroup)}
        {between === undefined || between === null ? null : (
          // The hairline belongs to this block, as a group's does, and is left
          // off when nothing was drawn above it to be divided from.
          <div
            className={cn('flex w-full flex-col items-center gap-1', groups.length > 0 && 'mt-2 border-t border-line pt-2')}
          >
            {between}
          </div>
        )}
        {betweenNext === undefined || betweenNext === null ? null : (
          // Its own hairline, left off when nothing at all was drawn above it.
          <div
            className={cn(
              'flex w-full flex-col items-center gap-1',
              (groups.length > 0 || (between !== undefined && between !== null)) && 'mt-2 border-t border-line pt-2',
            )}
          >
            {betweenNext}
          </div>
        )}
        {groupsAfter.map(drawGroup)}
      </nav>
      {children === undefined ? null : (
        <div className="flex w-full shrink-0 flex-col items-center gap-1 border-t border-line pt-2">{children}</div>
      )}
    </>
  )
}
